import React, { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_OTP_LENGTH = 4;

const buildEmptyDigits = (length) => Array.from({ length }, () => "");

const OtpDigitInput = ({ value, onChange, error, autoFocus, length = DEFAULT_OTP_LENGTH, inputClassName = "bt-forgot-otp-box" }) => {
  const otpLength = Math.max(1, Number(length) || DEFAULT_OTP_LENGTH);
  const emptyDigits = useMemo(() => buildEmptyDigits(otpLength), [otpLength]);
  const [digits, setDigits] = useState(emptyDigits);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    const next = emptyDigits.map((_, index) => (value[index] ? value[index] : ""));
    setDigits(next);
  }, [value, emptyDigits]);

  useEffect(() => {
    setActiveIndex(0);
  }, [otpLength]);

  useEffect(() => {
    if (!autoFocus) {
      return undefined;
    }
    const timer = window.setTimeout(() => inputRefs.current[0]?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [autoFocus, otpLength]);

  const emitChange = (nextDigits) => {
    onChange(nextDigits.join("").replace(/\D/g, "").slice(0, otpLength));
  };

  const updateDigit = (index, raw) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    emitChange(next);
    if (digit && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
        emitChange(next);
        return;
      }
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        setActiveIndex(index - 1);
      }
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    }
    if (event.key === "ArrowRight" && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, otpLength);
    if (!pasted) {
      return;
    }
    const next = emptyDigits.map((_, i) => pasted[i] || "");
    setDigits(next);
    emitChange(next);
    const focusIndex = Math.min(pasted.length, otpLength - 1);
    inputRefs.current[focusIndex]?.focus();
    setActiveIndex(focusIndex);
  };

  const boxesClassName = otpLength > 4 ? "bt-forgot-otp-boxes bt-forgot-otp-boxes--compact" : "bt-forgot-otp-boxes";

  return (
    <div>
      <div className={boxesClassName} onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={1}
            className={`${inputClassName} ${activeIndex === index ? "is-active" : ""} ${error ? "has-error" : ""}`}
            value={digit}
            aria-label={`Digit ${index + 1} of ${otpLength}`}
            onFocus={() => setActiveIndex(index)}
            onChange={(e) => updateDigit(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
          />
        ))}
      </div>
      {error ? (
        <p className="bt-forgot-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export const OTP_INPUT_LENGTH = DEFAULT_OTP_LENGTH;
export default OtpDigitInput;
